import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { SyncSource, SyncSourceCreatePayload, SyncResult } from '@shared/types/sync';

// ─── Sync Sources List ───
async function fetchSyncSources(): Promise<SyncSource[]> {
  const { data } = await api.get<SyncSource[]>('/sync/sources');
  return data;
}

export function useSyncSources() {
  return useQuery({
    queryKey: ['sync-sources'],
    queryFn: fetchSyncSources,
  });
}

// ─── Create Sync Source ───
export function useCreateSyncSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SyncSourceCreatePayload) => {
      const { data } = await api.post('/sync/sources', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sync-sources'] });
    },
  });
}

// ─── Toggle Sync Source Active ───
export function useToggleSyncSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sourceId: number) => {
      const { data } = await api.patch(`/sync/sources/${sourceId}/toggle`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sync-sources'] });
    },
  });
}

// ─── Run Manual Sync ───
export function useRunSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<SyncResult[]> => {
      const { data } = await api.post<SyncResult[]>('/sync/run');
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sync-sources'] });
      qc.invalidateQueries({ queryKey: ['towns'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}
